/**
 * Product Floating Bar
 * Handles visibility, cart actions, and urgency message rotation
 */

(function() {
  'use strict';

  // Store initialized instances
  const instances = new Map();

  /**
   * Initialize a floating bar instance
   * @param {string} blockId - The block ID
   * @param {Object} options - Configuration options
   */
  window.initProductFloatingBar = function(blockId, options = {}) {
    const bar = document.querySelector(`#product-floating-bar-${blockId}`);
    if (!bar || instances.has(blockId)) return;

    const instance = new ProductFloatingBar(bar, options);
    instances.set(blockId, instance);
    return instance;
  };

  class ProductFloatingBar {
    constructor(element, options = {}) {
      this.element = element;
      this.options = {
        urgencyInterval: options.urgencyInterval || 3000,
        visibilityMode: options.visibilityMode || 'always'
      };

      this.variantId = element.dataset.variantId;
      this.productAvailable = element.dataset.productAvailable === 'true';
      
      this.urgencyBanner = element.querySelector('[data-urgency-banner]');
      this.urgencyItems = element.querySelectorAll('[data-urgency-item]');
      this.buttons = element.querySelectorAll('[data-floating-btn]');
      
      this.urgencyIndex = 0;
      this.urgencyTimer = null;
      this.observer = null;
      this.lastScrollY = 0;
      this.scrollHandler = null;
      this.isDrawerOpen = false;

      this.bodyObserver = null;

      this.init();
    }

    init() {
      this.setupVisibility();
      this.setupButtonListeners();
      this.setupVariantChangeListener();
      this.setupDrawerListeners();
      this.setupBodyClassObserver();
      
      if (this.urgencyItems.length > 1) {
        this.startUrgencyRotation();
      }

      // Initialize Lucide icons if available
      if (typeof lucide !== 'undefined') {
        lucide.createIcons({
          icons: {},
          attrs: { 'stroke-width': 2 }
        });
      }
    }

    /**
     * Setup listeners for drawers to hide floating bar
     */
    setupDrawerListeners() {
      // List of events that indicate a drawer is opening
      const openEvents = [
        'cart:opened',
        'wishlist:opened',
        'search:opened',
        'menu:opened',
        'offers:opened',
        'modal:open'
      ];

      // List of events that indicate a drawer is closing
      const closeEvents = [
        'cart:closed',
        'wishlist:closed',
        'search:closed',
        'menu:closed',
        'offers:closed',
        'modal:close'
      ];

      // Hide on open
      openEvents.forEach(event => {
        window.addEventListener(event, () => {
          this.isDrawerOpen = true;
          this.hide();
        });
      });

      // Restore visibility check on close
      closeEvents.forEach(event => {
        window.addEventListener(event, () => {
          this.isDrawerOpen = false;
          // Trigger a scroll check or visibility check
          if (this.scrollHandler) {
            this.scrollHandler();
          } else {
            this.setupVisibility();
          }
        });
      });
      
      // Additional safety for native select dropdowns (iOS region selector drawer)
      document.querySelectorAll('.localization-switcher__select, select[name="country_code"]').forEach(select => {
        select.addEventListener('focus', () => {
          this.isDrawerOpen = true;
          this.hide();
        });
        select.addEventListener('blur', () => {
          this.isDrawerOpen = false;
          if (this.scrollHandler) {
            this.scrollHandler();
          } else {
            this.setupVisibility();
          }
        });
      });
    }

    /**
     * Observe body class changes to reactively hide/show when drawers or popups open/close
     */
    setupBodyClassObserver() {
      const overlayClasses = ['drawer-open', 'newsletter-popup-open', 'cart-drawer-open', 'wishlist-drawer-open', 'menu-drawer-open', 'search-drawer-open'];

      this.bodyObserver = new MutationObserver(() => {
        const hasOverlayClass = overlayClasses.some(cls => document.body.classList.contains(cls));
        const hasHiddenOverflow = document.body.style.overflow === 'hidden';
        
        if (hasOverlayClass || hasHiddenOverflow) {
          this.isDrawerOpen = true;
          this.hide();
        } else if (this.isDrawerOpen) {
          this.isDrawerOpen = false;
          if (this.scrollHandler) {
            this.scrollHandler();
          } else {
            this.show();
          }
        }
      });

      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
    }

    /**
     * Setup visibility based on selected mode
     */
    setupVisibility() {
      // Don't show if a drawer or popup is open
      if (this.isDrawerOpen || document.body.style.overflow === 'hidden' || document.body.classList.contains('cart-drawer-open') || document.body.classList.contains('wishlist-drawer-open') || document.body.classList.contains('menu-drawer-open') || document.body.classList.contains('search-drawer-open') || document.body.classList.contains('drawer-open') || document.body.classList.contains('newsletter-popup-open')) {
        this.hide();
        return;
      }

      if (this.options.visibilityMode === 'scroll_up') {
        this.setupScrollUpVisibility();
      } else {
        // Default: always visible
        this.show();
      }
    }

    /**
     * Show when scrolling up, hide when scrolling down
     */
    setupScrollUpVisibility() {
      this.lastScrollY = window.scrollY;
      
      // Remove existing handler if any
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
      
      this.scrollHandler = () => {
        if (this.isDrawerOpen) return;
        
        const currentScrollY = window.scrollY;
        
        if (currentScrollY < this.lastScrollY || currentScrollY < 100) {
          this.show();
        } else {
          this.hide();
        }
        
        this.lastScrollY = currentScrollY;
      };
      
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
      
      // Show initially
      this.show();
    }

    /**
     * Show the floating bar
     */
    show() {
      // Final safety check for open drawers/popups
      if (this.isDrawerOpen || document.body.style.overflow === 'hidden' || document.body.classList.contains('cart-drawer-open') || document.body.classList.contains('wishlist-drawer-open') || document.body.classList.contains('drawer-open') || document.body.classList.contains('newsletter-popup-open')) {
        this.hide();
        return;
      }
      this.element.classList.add('is-visible');
    }

    /**
     * Hide the floating bar
     */
    hide() {
      this.element.classList.remove('is-visible');
    }

    /**
     * Setup click listeners for buttons
     */
    setupButtonListeners() {
      this.buttons.forEach((button) => {
        const btnType = button.dataset.floatingBtn;

        button.addEventListener('click', (e) => {
          // Custom links are handled by browser
          if (btnType === 'custom') return;
          
          e.preventDefault();

          switch (btnType) {
            case 'add_to_cart':
              this.handleAddToCart(button);
              break;
            case 'buy_now':
              this.handleBuyNow(button);
              break;
          }
        });
      });
    }

    /**
     * Listen for variant changes and update button states
     */
    setupVariantChangeListener() {
      // Listen for variant change events
      document.addEventListener('variant:changed', (e) => {
        this.handleVariantChange(e.detail);
      });

      // Also listen for Shopify's native variant change
      document.addEventListener('change', (e) => {
        if (e.target.matches('input[name="id"], select[name="id"]')) {
          const variantId = e.target.value;
          this.updateVariantId(variantId);
          const inferredAvailability = this.resolveAvailabilityFromInput(e.target);
          if (inferredAvailability !== null) {
            this.updateAvailability(inferredAvailability);
          }
        }
      });
    }

    /**
     * Handle variant change
     * @param {Object} detail - Variant change detail
     */
    handleVariantChange(detail) {
      if (detail && detail.variant) {
        this.updateVariantId(detail.variant.id);
        this.updateAvailability(detail.variant.available);
      }
    }

    /**
     * Update the variant ID on all buttons
     * @param {string} variantId - New variant ID
     */
    updateVariantId(variantId) {
      this.variantId = variantId;
      this.element.dataset.variantId = variantId;
      
      this.buttons.forEach((button) => {
        if (button.dataset.variantId !== undefined) {
          button.dataset.variantId = variantId;
        }
      });
      
      // Update wishlist buttons too
      const wishlistBtns = this.element.querySelectorAll('[data-wishlist-btn]');
      wishlistBtns.forEach(btn => {
        btn.dataset.variantId = variantId;
        // Also update price data attributes if variant data is available
        // Note: We might not have full variant object here to update price
      });
    }

    /**
     * Infer variant availability from native variant controls when possible.
     * @param {HTMLElement} input - Variant input/select element
     * @returns {boolean|null}
     */
    resolveAvailabilityFromInput(input) {
      if (!input) return null;

      if (input.dataset && input.dataset.available !== undefined) {
        return input.dataset.available === 'true';
      }

      if (input.tagName === 'SELECT') {
        const selectedOption = input.selectedOptions && input.selectedOptions[0];
        if (!selectedOption) return null;

        if (selectedOption.dataset && selectedOption.dataset.available !== undefined) {
          return selectedOption.dataset.available === 'true';
        }

        if (selectedOption.disabled === true) {
          return false;
        }
      }

      if (input.disabled === true) {
        return false;
      }

      return null;
    }

    /**
     * Update button availability states
     * @param {boolean} available - Whether variant is available
     */
    updateAvailability(available) {
      const isAvailable = available === true;
      this.productAvailable = isAvailable;
      this.element.dataset.productAvailable = String(isAvailable);
      
      this.buttons.forEach((button) => {
        const btnType = button.dataset.floatingBtn;
        
        if (btnType === 'add_to_cart' || btnType === 'buy_now') {
          button.disabled = !isAvailable;
          const textEl = button.querySelector('[data-btn-text]');
          if (textEl) {
            if (!isAvailable) {
              textEl.textContent = 'Sold Out';
            } else {
              // Restore original text based on button type
              textEl.textContent = btnType === 'add_to_cart' ? 'Add to Cart' : 'Buy Now';
            }
          }
        }
      });
    }

    /**
     * Handle Add to Cart button click
     * @param {HTMLElement} button - The clicked button
     */
    async handleAddToCart(button) {
      if (button.disabled) return;

      const variantId = button.dataset.variantId || this.variantId;
      if (!variantId) return;

      this.setButtonLoading(button, true);

      try {
        // Use Cart API if available
        if (window.CartAPI) {
          await window.CartAPI.addItem(parseInt(variantId, 10), 1);
        } else {
          // Fallback to fetch
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: parseInt(variantId, 10),
                quantity: 1
              }]
            })
          });

          if (!response.ok) {
            throw new Error('Failed to add to cart');
          }
          
          const data = await response.json();
          // Dispatch legacy event for fallback
          window.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { cart: data } // Note: CartAPI usually fetches full cart, here we just have added item response, but event listener usually fetches cart
          }));
        }

        this.setButtonSuccess(button);
        
        // Dispatch events that open the drawer
        // cart:updated is dispatched by CartAPI
        // cart:item-added triggers the drawer open in cart-drawer.js
        document.dispatchEvent(new CustomEvent('cart:item-added'));

      } catch (error) {
        console.error('Add to cart error:', error);
        this.setButtonLoading(button, false);
      }
    }

    /**
     * Handle Buy Now button click
     * @param {HTMLElement} button - The clicked button
     */
    async handleBuyNow(button) {
      if (button.disabled) return;

      const variantId = button.dataset.variantId || this.variantId;
      if (!variantId) return;

    // Prefer native Shopify dynamic checkout flow so third-party
    // integrations (e.g. Magic Checkout) can hook into the expected path.
    const dynamicCheckoutButton = document.querySelector(
      '.product-add-to-cart-block__dynamic-checkout .shopify-payment-button__button, .shopify-payment-button__button'
    );
    if (dynamicCheckoutButton && !dynamicCheckoutButton.disabled) {
      dynamicCheckoutButton.click();
      return;
    }

      this.setButtonLoading(button, true);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId, 10),
              quantity: 1
            }]
          })
        });

        if (!response.ok) {
          throw new Error('Failed to add to cart');
        }

        // Redirect to checkout
        window.location.href = '/checkout';

      } catch (error) {
        console.error('Buy now error:', error);
        this.setButtonLoading(button, false);
      }
    }

    /**
     * Set button loading state
     * @param {HTMLElement} button - The button element
     * @param {boolean} loading - Whether loading
     */
    setButtonLoading(button, loading) {
      if (loading) {
        button.classList.add('is-loading');
        button.disabled = true;
      } else {
        button.classList.remove('is-loading');
        button.disabled = !this.productAvailable;
      }
    }

    /**
     * Set button success state temporarily
     * @param {HTMLElement} button - The button element
     */
    setButtonSuccess(button) {
      button.classList.remove('is-loading');
      button.classList.add('is-success');
      
      const textEl = button.querySelector('[data-btn-text]');
      const originalText = textEl ? textEl.textContent : '';
      
      if (textEl) {
        textEl.textContent = 'Added!';
      }

      setTimeout(() => {
        button.classList.remove('is-success');
        button.disabled = !this.productAvailable;
        if (textEl) {
          textEl.textContent = originalText;
        }
      }, 2000);
    }

    /**
     * Start urgency message rotation
     */
    startUrgencyRotation() {
      this.urgencyTimer = setInterval(() => {
        this.rotateUrgencyMessage();
      }, this.options.urgencyInterval);
    }

    /**
     * Rotate to next urgency message
     */
    rotateUrgencyMessage() {
      if (this.urgencyItems.length <= 1) return;

      // Remove active from current
      this.urgencyItems[this.urgencyIndex].classList.remove('is-active');

      // Move to next
      this.urgencyIndex = (this.urgencyIndex + 1) % this.urgencyItems.length;

      // Add active to new
      this.urgencyItems[this.urgencyIndex].classList.add('is-active');
    }

    /**
     * Destroy the instance and cleanup
     */
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }
      
      if (this.bodyObserver) {
        this.bodyObserver.disconnect();
      }
      
      if (this.urgencyTimer) {
        clearInterval(this.urgencyTimer);
      }
      
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
      }
    }
  }

  // Auto-initialize on DOMContentLoaded for bars without explicit init
  document.addEventListener('DOMContentLoaded', function() {
    const bars = document.querySelectorAll('[data-product-floating-bar]');
    bars.forEach((bar) => {
      const blockId = bar.id.replace('product-floating-bar-', '');
      if (!instances.has(blockId)) {
        window.initProductFloatingBar(blockId);
      }
    });
  });
})();
